import { useState, useCallback, useRef } from 'react';
import { message } from 'antd';

export interface FileUploadItem {
  id: string;
  file: File;
  type: 'image' | 'audio' | 'document';
  preview?: React.ReactNode;
  url?: string;
  status: 'pending' | 'uploading' | 'success' | 'error';
  progress?: number;
}

export interface FileValidationResult {
  valid: boolean;
  message?: string;
}

export interface UseFileUploadOptions {
  maxFileSize?: number; // 默认10MB
  maxFiles?: number; // 默认5个文件
  supportedImageTypes?: string[];
  supportedAudioTypes?: string[];
  supportedDocumentTypes?: string[];
  maxAudioDuration?: number; // 默认5分钟
  onFileAdd?: (file: FileUploadItem) => void;
  onFileRemove?: (fileId: string) => void;
  onValidationError?: (error: string) => void;
}

const defaultOptions: Required<Omit<UseFileUploadOptions, 'onFileAdd' | 'onFileRemove' | 'onValidationError'>> = {
  maxFileSize: 10 * 1024 * 1024, // 10MB
  maxFiles: 5,
  supportedImageTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'],
  supportedAudioTypes: ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/m4a', 'audio/ogg', 'audio/webm'],
  supportedDocumentTypes: ['application/pdf'],
  maxAudioDuration: 300, // 5分钟
};

export const useFileUpload = (options: UseFileUploadOptions = {}) => {
  const opts = { ...defaultOptions, ...options };
  const [files, setFiles] = useState<FileUploadItem[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  /**
   * 获取文件类型
   */
  const getFileType = useCallback((file: File): 'image' | 'audio' | 'document' => {
    if (opts.supportedImageTypes.includes(file.type)) {
      return 'image';
    } else if (opts.supportedAudioTypes.includes(file.type)) {
      return 'audio';
    } else if (opts.supportedDocumentTypes.includes(file.type)) {
      return 'document';
    }
    return 'document'; // 默认
  }, [opts]);

  /**
   * 验证文件
   */
  const validateFile = useCallback(async (file: File): Promise<FileValidationResult> => {
    // 检查文件大小
    if (file.size > opts.maxFileSize) {
      return {
        valid: false,
        message: `文件过大: ${(file.size / 1024 / 1024).toFixed(1)}MB > ${opts.maxFileSize / 1024 / 1024}MB`
      };
    }

    // 检查文件类型
    const fileType = file.type;
    const allSupportedTypes = [
      ...opts.supportedImageTypes,
      ...opts.supportedAudioTypes,
      ...opts.supportedDocumentTypes
    ];

    if (!allSupportedTypes.includes(fileType)) {
      return {
        valid: false,
        message: `不支持的文件类型: ${fileType}`
      };
    }

    // 检查音频时长（如果是音频文件）
    if (opts.supportedAudioTypes.includes(fileType)) {
      try {
        const duration = await getAudioDuration(file);
        if (duration > opts.maxAudioDuration) {
          return {
            valid: false,
            message: `音频时长过长: ${(duration/60).toFixed(1)}分钟 > ${opts.maxAudioDuration/60}分钟`
          };
        }
      } catch (error) {
        // 无法获取音频时长，跳过验证
      }
    }

    return { valid: true };
  }, [opts]);

  /**
   * 获取音频时长
   */
  const getAudioDuration = useCallback((file: File): Promise<number> => {
    return new Promise((resolve, reject) => {
      const audio = document.createElement('audio');
      audio.preload = 'metadata';

      const timeout = setTimeout(() => {
        reject(new Error('音频加载超时'));
      }, 10000);

      audio.onloadedmetadata = () => {
        clearTimeout(timeout);
        const duration = audio.duration;
        if (isNaN(duration) || duration === Infinity) {
          reject(new Error('无效的音频时长'));
        } else {
          resolve(duration);
        }
      };

      audio.onerror = () => {
        clearTimeout(timeout);
        reject(new Error('无法加载音频文件'));
      };

      try {
        const objectURL = URL.createObjectURL(file);
        audio.src = objectURL;
        
        // 清理URL
        audio.addEventListener('loadedmetadata', () => {
          URL.revokeObjectURL(objectURL);
        }, { once: true });
      } catch (error) {
        clearTimeout(timeout);
        reject(error);
      }
    });
  }, []);

  /**
   * 创建文件预览
   */
  const createFilePreview = useCallback(async (file: File, type: 'image' | 'audio' | 'document') => {
    if (type === 'image') {
      return new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target?.result as string);
        reader.readAsDataURL(file);
      });
    }
    return URL.createObjectURL(file);
  }, []);

  /**
   * 添加文件
   */
  const addFiles = useCallback(async (newFiles: File[]) => {
    // 检查文件数量限制
    if (files.length + newFiles.length > opts.maxFiles) {
      const errorMsg = `文件数量超限：最多支持${opts.maxFiles}个文件`;
      options.onValidationError?.(errorMsg);
      message.error(errorMsg);
      return;
    }

    const validFiles: FileUploadItem[] = [];

    for (const file of newFiles) {
      const validation = await validateFile(file);
      
      if (!validation.valid) {
        const errorMsg = `文件 ${file.name}: ${validation.message}`;
        options.onValidationError?.(errorMsg);
        message.error(errorMsg);
        continue;
      }

      const fileType = getFileType(file);
      const previewUrl = await createFilePreview(file, fileType);

      const fileItem: FileUploadItem = {
        id: `file-${Date.now()}-${Math.random()}`,
        file,
        type: fileType,
        url: previewUrl,
        status: 'pending'
      };

      validFiles.push(fileItem);
      options.onFileAdd?.(fileItem);
    }

    if (validFiles.length > 0) {
      setFiles(prevFiles => [...prevFiles, ...validFiles]);
      message.success(`成功添加 ${validFiles.length} 个文件`);
    }
  }, [files.length, opts.maxFiles, validateFile, getFileType, createFilePreview, options]);

  /**
   * 删除文件
   */
  const removeFile = useCallback((fileId: string) => {
    setFiles(prevFiles => {
      const fileToRemove = prevFiles.find(f => f.id === fileId);
      if (fileToRemove?.url && fileToRemove.url.startsWith('blob:')) {
        URL.revokeObjectURL(fileToRemove.url);
      }
      options.onFileRemove?.(fileId);
      return prevFiles.filter(f => f.id !== fileId);
    });
  }, [options]);

  /**
   * 清除所有文件
   */
  const clearFiles = useCallback(() => {
    files.forEach(file => {
      if (file.url && file.url.startsWith('blob:')) {
        URL.revokeObjectURL(file.url);
      }
    });
    setFiles([]);
  }, [files]);

  /**
   * 打开文件选择器
   */
  const openFileSelector = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  /**
   * 处理文件输入变化
   */
  const handleFileInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);
    if (selectedFiles.length > 0) {
      addFiles(selectedFiles);
    }
    // 清空input值，允许重复选择同一文件
    event.target.value = '';
  }, [addFiles]);

  /**
   * 更新文件状态
   */
  const updateFileStatus = useCallback((fileId: string, status: FileUploadItem['status'], progress?: number) => {
    setFiles(prevFiles => 
      prevFiles.map(file => 
        file.id === fileId 
          ? { ...file, status, progress }
          : file
      )
    );
  }, []);

  /**
   * 获取支持的文件类型字符串（用于input accept）
   */
  const getAcceptString = useCallback(() => {
    return [
      ...opts.supportedImageTypes,
      ...opts.supportedAudioTypes,
      ...opts.supportedDocumentTypes
    ].join(',');
  }, [opts]);

  return {
    // 状态
    files,
    isUploading,
    
    // 方法
    addFiles,
    removeFile,
    clearFiles,
    openFileSelector,
    updateFileStatus,
    
    // 工具方法
    validateFile,
    getFileType,
    getAcceptString,
    
    // 事件处理器
    handleFileInputChange,
    
    // Ref
    fileInputRef,
    
    // 配置
    options: opts
  };
}; 